import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, type SortState, type ColumnDef } from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { useEffectiveFilters } from '@/hooks/useEffectiveFilters';
import { useStaffUsers } from '@/features/contacts/hooks/useStaffUsers';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';
import {
  useJobsList,
  useBulkUpdateRecurrence,
  type Job,
  type JobStatus,
  type JobPriority,
  type JobsListParams,
} from './hooks/useJobs';
import { BulkRecurrenceModal } from './BulkRecurrenceModal';

/** Map job status to display label */
const STATUS_LABELS: Record<JobStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  waiting_on_client: 'Waiting on Client',
  in_review: 'In Review',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

/** Map job status to badge variant */
const STATUS_VARIANTS: Record<JobStatus, BadgeVariant> = {
  not_started: 'default',
  in_progress: 'info',
  on_hold: 'warning',
  waiting_on_client: 'warning',
  in_review: 'info',
  complete: 'success',
  cancelled: 'danger',
};

/** Map priority to badge variant */
const PRIORITY_VARIANTS: Record<JobPriority, BadgeVariant> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'waiting_on_client', label: 'Waiting on Client' },
  { value: 'in_review', label: 'In Review' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

/**
 * Jobs list page with filtering, sorting, and pagination.
 * Validates: Requirements 8.17
 */
export function JobsListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { toast } = useToast();
  const canManageRecurrence = can(PERMISSIONS.JOBS_MANAGE_RECURRENCE);
  const { data: jobTypes } = useSystemLookups('job_types');
  const { data: practiceEntities } = useSystemLookups('practice_entities');
  const { getFilterValues } = useEffectiveFilters();
  const allowedJobTypes = getFilterValues('jobs', 'job_type');
  const { data: staffUsers } = useStaffUsers();

  const [params, setParams] = useState<JobsListParams>({
    page: 1,
    pageSize: 25,
    status: '',
    priority: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
    search: '',
  });
  const [showAll, setShowAll] = useState(false);
  const [futureRecurring, setFutureRecurring] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false);

  const { data, isLoading } = useJobsList({
    ...params,
    showAll: showAll || undefined,
    futureRecurring: futureRecurring || undefined,
  });
  const bulkUpdateRecurrence = useBulkUpdateRecurrence();

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [
    params.page,
    params.pageSize,
    params.status,
    params.priority,
    params.search,
    params.jobType,
    params.practiceEntityId,
    params.assignedRole,
    params.assignedUserId,
    showAll,
    futureRecurring,
  ]);

  const [sort, setSort] = useState<SortState>({
    column: 'createdAt',
    direction: 'desc',
  });

  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setParams((prev) => ({
      ...prev,
      sortBy: newSort.direction ? newSort.column : undefined,
      sortDirection: newSort.direction ?? undefined,
      page: 1,
    }));
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setParams((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setParams((prev) => ({
      ...prev,
      status: e.target.value as JobStatus | '',
      page: 1,
    }));
  }, []);

  const handlePriorityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setParams((prev) => ({
      ...prev,
      priority: e.target.value as JobPriority | '',
      page: 1,
    }));
  }, []);

  const columns: ColumnDef<Job>[] = [
    {
      id: 'title',
      header: 'Job Title',
      sortable: true,
      cell: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/jobs/${row.id}`)}
          className="text-left text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
        >
          {row.title}
        </button>
      ),
    },
    {
      id: 'jobCode',
      header: 'Code',
      sortable: false,
      cell: (row) =>
        row.jobCode ? (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {row.jobCode}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      id: 'clientName',
      header: 'Client',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-700 dark:text-gray-300 truncate block max-w-[140px]">
          {row.clientName || '—'}
        </span>
      ),
    },
    {
      id: 'jobType',
      header: 'Type',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600 dark:text-gray-400 truncate block max-w-[100px]">
          {row.jobType || '—'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      // Surface the current workflow STAGE as the user-facing status (the
      // underlying job status still drives system processes). Terminal statuses
      // (complete/cancelled) and jobs without a stage fall back to the status
      // label. The badge colour continues to reflect the real status.
      cell: (row) => {
        const showStage =
          !!row.currentStageName && row.status !== 'complete' && row.status !== 'cancelled';
        return (
          <Badge variant={STATUS_VARIANTS[row.status]} size="sm">
            {showStage ? row.currentStageName : STATUS_LABELS[row.status]}
          </Badge>
        );
      },
    },
    {
      id: 'priority',
      header: 'Priority',
      sortable: true,
      cell: (row) => (
        <Badge variant={PRIORITY_VARIANTS[row.priority]} size="sm">
          {row.priority.charAt(0).toUpperCase() + row.priority.slice(1)}
        </Badge>
      ),
    },
    {
      id: 'assignedAccountantName',
      header: 'Assignee',
      sortable: true,
      cell: (row) => (
        <span className="text-xs text-gray-600 dark:text-gray-400 truncate block max-w-[110px]">
          {row.assignedAccountantName ?? 'Unassigned'}
        </span>
      ),
    },
    {
      id: 'progressPercentage',
      header: 'Progress',
      sortable: true,
      align: 'center',
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          <div className="h-1.5 w-12 rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-1.5 rounded-full bg-brand-500"
              style={{ width: `${row.progressPercentage}%` }}
            />
          </div>
          <span className="text-[11px] text-gray-500">{row.progressPercentage}%</span>
        </div>
      ),
    },
    {
      id: 'dueDate',
      header: 'Due Date',
      sortable: true,
      cell: (row) => {
        if (!row.dueDate) return <span className="text-gray-400 text-xs">—</span>;
        const isOverdue = row.isOverdue;
        return (
          <span
            className={cn(
              'text-xs',
              isOverdue ? 'font-medium text-danger-600' : 'text-gray-600 dark:text-gray-400',
            )}
          >
            {new Date(row.dueDate).toLocaleDateString()}
            {isOverdue && <span className="ml-0.5 text-[10px]">(Overdue)</span>}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: (row) =>
        can(PERMISSIONS.JOBS_UPDATE) && row.status !== 'complete' && row.status !== 'cancelled' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/jobs/${row.id}/edit`);
            }}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Jobs</h1>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Manage and track all jobs across your practice
          </p>
        </div>
        <div className="flex items-center gap-3">
          {can(PERMISSIONS.JOBS_READ) && (
            <Button variant="outline" onClick={() => navigate('/jobs/kanban')}>
              Kanban Board
            </Button>
          )}
          {can(PERMISSIONS.JOBS_CREATE) && (
            <Button onClick={() => navigate('/jobs/new')}>Create Job</Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={params.status}
              onChange={handleStatusChange}
            />
          </div>
          <div className="w-40">
            <Select
              label="Priority"
              options={PRIORITY_OPTIONS}
              value={params.priority}
              onChange={handlePriorityChange}
            />
          </div>
          <div className="w-40">
            <Select
              label="Job Type"
              options={(jobTypes ?? [])
                .filter((jt) => jt.isActive)
                .filter((jt) => !allowedJobTypes || allowedJobTypes.includes(jt.code))
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((jt) => ({ value: jt.code, label: jt.label }))}
              value={params.jobType ?? ''}
              onChange={(e) =>
                setParams((prev) => ({ ...prev, jobType: e.target.value || undefined, page: 1 }))
              }
              placeholder="All Types"
            />
          </div>
          <div className="w-40">
            <Select
              label="Practice Entity"
              options={(practiceEntities ?? [])
                .filter((pe) => pe.isActive)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((pe) => ({ value: pe.code, label: pe.label }))}
              value={params.practiceEntityId ?? ''}
              onChange={(e) =>
                setParams((prev) => ({
                  ...prev,
                  practiceEntityId: e.target.value || undefined,
                  page: 1,
                }))
              }
              placeholder="All Entities"
            />
          </div>
          <div className="w-40">
            <Select
              label="Assigned Role"
              options={[
                { value: '', label: 'All Roles' },
                { value: 'partner', label: 'Partner' },
                { value: 'manager', label: 'Manager' },
                { value: 'accountant', label: 'Accountant' },
              ]}
              value={params.assignedRole ?? ''}
              onChange={(e) =>
                setParams((prev) => ({
                  ...prev,
                  assignedRole: (e.target.value || undefined) as JobsListParams['assignedRole'],
                  assignedUserId: undefined,
                  page: 1,
                }))
              }
            />
          </div>
          {params.assignedRole && (
            <div className="w-40">
              <Select
                label="Assigned To"
                options={(staffUsers ?? []).map((u) => ({ value: u.id, label: u.name || u.email }))}
                value={params.assignedUserId ?? ''}
                onChange={(e) =>
                  setParams((prev) => ({
                    ...prev,
                    assignedUserId: e.target.value || undefined,
                    page: 1,
                  }))
                }
                placeholder="Anyone"
              />
            </div>
          )}
          {can(PERMISSIONS.DATA_SCOPE_VIEW_ALL) && (
            <label className="ml-auto flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => {
                  setShowAll(e.target.checked);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
              />
              Show All Records
            </label>
          )}
          {canManageRecurrence && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={futureRecurring}
                onChange={(e) => {
                  setFutureRecurring(e.target.checked);
                  setParams((prev) => ({ ...prev, page: 1 }));
                }}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
              />
              Future Recurring
            </label>
          )}
        </div>
      </Card>

      {canManageRecurrence && futureRecurring && selectedKeys.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-2 dark:border-border-dark dark:bg-surface-dark-secondary">
          <p className="text-sm text-gray-700 dark:text-gray-300">{selectedKeys.size} selected</p>
          <Button size="sm" onClick={() => setRecurrenceModalOpen(true)}>
            Update recurrence
          </Button>
        </div>
      )}

      {/* Data table */}
      <DataTable<Job>
        data={data?.items ?? []}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={handleSortChange}
        loading={isLoading}
        compact
        emptyMessage="No jobs found matching your filters"
        filterValue={params.search}
        onFilterChange={handleFilterChange}
        filterPlaceholder="Search jobs by title, code, or type..."
        selectable={canManageRecurrence && futureRecurring}
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        pagination={
          data ? { page: data.page, pageSize: data.pageSize, total: data.total } : undefined
        }
        onPageChange={(page) => setParams((prev) => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) => setParams((prev) => ({ ...prev, pageSize, page: 1 }))}
      />

      <BulkRecurrenceModal
        open={recurrenceModalOpen}
        jobCount={selectedKeys.size}
        saving={bulkUpdateRecurrence.isPending}
        onClose={() => setRecurrenceModalOpen(false)}
        onSave={(values) => {
          bulkUpdateRecurrence.mutate(
            {
              jobIds: Array.from(selectedKeys),
              isRecurring: values.isRecurring,
              recurrenceFrequency: values.isRecurring ? values.recurrenceFrequency : null,
              recurrenceNextDate: values.isRecurring ? values.recurrenceNextDate : null,
            },
            {
              onSuccess: (result) => {
                if (result.failed > 0) {
                  toast({
                    variant: 'warning',
                    title: `Updated ${result.successful} of ${result.total} jobs`,
                    description: `${result.failed} could not be updated.`,
                  });
                } else {
                  toast({
                    variant: 'success',
                    title: `Updated recurrence on ${result.successful} ${result.successful === 1 ? 'job' : 'jobs'}`,
                  });
                }
                setRecurrenceModalOpen(false);
                setSelectedKeys(new Set());
              },
              onError: (err) => {
                toast({
                  variant: 'error',
                  title: 'Could not update recurrence',
                  description: err instanceof Error ? err.message : undefined,
                });
              },
            },
          );
        }}
      />
    </div>
  );
}
